import React from 'react';
import { DatePicker, Form, Input, Modal, Radio, Select, Spin } from 'antd';
import { DOCUMENT_UI_DATETIME_FORMAT } from '../../../../utils/documentUiFormat';
import { CLOSING_PACKAGE_MODAL_TITLES } from '../../workspace/closingPackageModalKinds';
import { useClientReceiptModal } from '../../hooks/useClientReceiptModal';
import { ORIGINALS_PROBLEM_TYPE_OPTIONS } from '../../lib/originalsProblemTypes';
import { isClientReceiptCommentRequired } from '../../lib/clientReceiptModalValidation';
import ClosingPackageTaskModalContext from './ClosingPackageTaskModalContext';
import ClosingPackageTaskModalEventDateHint from './ClosingPackageTaskModalEventDateHint';
import ClosingPackageTaskModalFooter from './ClosingPackageTaskModalFooter';
import type { ClosingPackageTaskModalBaseProps } from './ClosingPackageFormationModal.types';

export type ClosingPackageClientReceiptModalProps = ClosingPackageTaskModalBaseProps & {
  onSuccess?: () => void | Promise<void>;
};

/**
 * IMP-001H-5A/5B — CONFIRM_CLIENT_RECEIPT (RECEIVED + PROBLEM return cycle).
 */
const ClosingPackageClientReceiptModal: React.FC<ClosingPackageClientReceiptModalProps> = ({
  open,
  onClose,
  applicationId,
  onSuccess,
}) => {
  const {
    form,
    projection,
    contextView,
    contextLoading,
    receiptResult,
    submitting,
    submitLabel,
    handleSubmit,
  } = useClientReceiptModal({
    applicationId,
    open,
    onClose,
    onSuccess,
  });

  const commentRequired = isClientReceiptCommentRequired(receiptResult);

  return (
    <Modal
      title={CLOSING_PACKAGE_MODAL_TITLES['client-receipt']}
      open={open}
      onCancel={onClose}
      destroyOnClose
      width={760}
      data-testid="client-receipt-modal"
      footer={
        <ClosingPackageTaskModalFooter
          onClose={onClose}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel={submitLabel}
          submitTestId="client-receipt-submit"
        />
      }
    >
      {contextLoading || !projection ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : (
        <>
          <ClosingPackageTaskModalContext contextView={contextView} />

          <Form form={form} layout="vertical">
            <Form.Item
              name="result"
              label="Результат проверки"
              rules={[{ required: true, message: 'Выберите результат' }]}
            >
              <Radio.Group data-testid="client-receipt-result">
                <Radio value="RECEIVED">Получено</Radio>
                <Radio value="PROBLEM">Не получено / требуется повторная отправка</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="receivedAt"
              label={receiptResult === 'PROBLEM' ? 'Дата проверки' : 'Дата подтверждения'}
              rules={[
                {
                  required: true,
                  message:
                    receiptResult === 'PROBLEM'
                      ? 'Укажите дату проверки'
                      : 'Укажите дату подтверждения',
                },
              ]}
            >
              <DatePicker
                showTime
                style={{ width: '100%' }}
                data-testid="client-receipt-date"
                format={DOCUMENT_UI_DATETIME_FORMAT}
              />
            </Form.Item>

            {receiptResult === 'PROBLEM' ? (
              <Form.Item
                name="problemType"
                label="Тип проблемы"
                rules={[{ required: true, message: 'Укажите тип проблемы' }]}
              >
                <Select
                  options={ORIGINALS_PROBLEM_TYPE_OPTIONS}
                  placeholder="Выберите тип проблемы"
                  data-testid="client-receipt-problem-type"
                />
              </Form.Item>
            ) : null}

            <Form.Item
              name="comment"
              label="Комментарий"
              rules={
                commentRequired
                  ? [
                      { required: true, message: 'Укажите комментарий' },
                      { whitespace: true, message: 'Комментарий не может быть пустым' },
                    ]
                  : []
              }
            >
              <Input.TextArea
                rows={3}
                maxLength={500}
                showCount
                placeholder={
                  commentRequired
                    ? 'Комментарий для журнала комплекта'
                    : 'Комментарий для журнала комплекта (необязательно)'
                }
                data-testid="client-receipt-comment"
              />
            </Form.Item>
          </Form>

          <ClosingPackageTaskModalEventDateHint />
        </>
      )}
    </Modal>
  );
};

export default ClosingPackageClientReceiptModal;
